import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../../App';
import { getPublicAssetPath } from '../../../Utils/assetUtils';
import { handleNudgeClick as trackNudgeClick } from '../utils/nudgeUtils';
import { BOB_NUDGES_CONFIG, NUDGE_ENUM_IMAGE_MAP } from './nudgesConfig';
import styles from './NudgesCard.module.css';

const CARD_GAP = 12;

// Always shown regardless of rule evaluation; excluded from the dynamic pool
// below so a triggered rule for one of these can't duplicate the static card.
const STATIC_NUDGE_CATEGORIES = [
  'SUGGEST_FIXED_DEPOSIT',
  'SUGGEST_MUTUAL_FUND',
  'SUGGEST_CREDIT_CARD_C',
];
const MAX_DYNAMIC_NUDGES = 3;

const getImageForNudge = (nudge: any): string | null => {
  if (nudge.nudgeUrl && nudge.nudgeUrl !== 'default') return nudge.nudgeUrl;
  const mapped = NUDGE_ENUM_IMAGE_MAP[nudge.category];
  return mapped ? getPublicAssetPath(mapped) : null;
};

interface NudgesCardProps {
  staticCategories?: string[];
  includeDynamicNudges?: boolean;
}

const NudgesCard: React.FC<NudgesCardProps> = ({
  staticCategories = STATIC_NUDGE_CATEGORIES,
  includeDynamicNudges = true,
}) => {
  const { nudges }: any = useContext(AppContext);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const dragMoved = useRef(false);

  const staticNudges = useMemo(
    () =>
      BOB_NUDGES_CONFIG.filter((config) =>
        staticCategories.includes(config.category)
      ).map((config) => ({
        nudgeId: `static-${config.category}`,
        nudgeUrl: getPublicAssetPath(config.imageUrl),
        category: config.category,
        triggerEvent: config.triggerEvent,
        meta: {},
      })),
    [staticCategories]
  );

  const visibleNudges = useMemo(() => {
    if (!includeDynamicNudges) return staticNudges;

    const dynamicNudges = (nudges || [])
      .filter((nudge: any) => !staticCategories.includes(nudge.category))
      .filter((nudge: any) => nudge.meta?.displayMode !== 'in_app_popup')
      .filter((nudge: any) => getImageForNudge(nudge) !== null)
      .slice(0, MAX_DYNAMIC_NUDGES);

    return [...dynamicNudges, ...staticNudges].filter((nudge) => {      
      return (
        nudge.category !== 'UPCOMING_EMI_BUT_NOT_ENOUGH_BALANCE' &&
        nudge.category !== 'SUGGEST_CREDIT_CARD_R' &&
        nudge.type !== 'INFO'
      );
    });
  }, [nudges, staticNudges, includeDynamicNudges, staticCategories]);
  
  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !visibleNudges.length) return;
    const cardWidth = scroller.scrollWidth / visibleNudges.length;
    const index = Math.round(scroller.scrollLeft / cardWidth);
    setActiveIndex(Math.max(0, Math.min(index, visibleNudges.length - 1)));
  }, [visibleNudges.length]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    isDragging.current = true;
    dragMoved.current = false;
    dragStartX.current = e.pageX - scroller.offsetLeft;
    dragScrollLeft.current = scroller.scrollLeft;
    // Disable snap so it doesn't fight the drag
    scroller.style.scrollSnapType = 'none';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current || !scrollerRef.current) return;
    e.preventDefault();
    dragMoved.current = true;
    const x = e.pageX - scrollerRef.current.offsetLeft;
    scrollerRef.current.scrollLeft =
      dragScrollLeft.current - (x - dragStartX.current);
  }, []);

  const handleMouseEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const scroller = scrollerRef.current;
    if (!scroller) return;

    // Smooth-snap to nearest card, then restore CSS snap
    const firstCard = scroller.children[0] as HTMLElement;
    if (firstCard && visibleNudges.length > 0) {
      const cardStep = firstCard.offsetWidth + CARD_GAP;
      const nearest = Math.round(scroller.scrollLeft / cardStep);
      const clamped = Math.max(0, Math.min(nearest, visibleNudges.length - 1));
      scroller.scrollTo({ left: clamped * cardStep, behavior: 'smooth' });
    }

    scroller.style.scrollSnapType = '';
  }, [visibleNudges.length]);

  const handleNudgeClick = useCallback((nudge: any) => {
    if (dragMoved.current) return;
    trackNudgeClick(nudge);
  }, []);

  if (!visibleNudges.length) return null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {/* <h3 className={styles.title}>Insights for you</h3> */}
      </div>
      <div
        className={styles.scroller}
        ref={scrollerRef}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseEnd}
        onMouseLeave={handleMouseEnd}
      >
        {visibleNudges.map((nudge: any) => {
          const imageSrc = getImageForNudge(nudge) as string;
          return (
            <div
              key={nudge.nudgeId}
              className={styles.card}
              onClick={() => handleNudgeClick(nudge)}
            >
              <img
                src={imageSrc}
                alt={nudge.title || ''}
                className={styles.cardImage}
                draggable={false}
              />
            </div>
          );
        })}
      </div>
      <div className={styles.dots}>
        {visibleNudges.map((_: any, index: number) => (
          <span
            key={index}
            className={`${styles.dot} ${index === activeIndex ? styles.dotActive : ''}`}
          />
        ))}
      </div>
    </div>
  );
};

export default NudgesCard;
